import React, { useState, useEffect } from 'react'
import { KTSVG, toAbsoluteUrl } from '../../../_metronic/helpers';


type Props = {
  className: string
}

type DashboardData = {
  total_booking_count: string;
  pending_count: string;
  collect_to_shipment_count: string;
  shipment_started_count: string;
  shipment_ended_count: string;
  delivered_count: string;
}

const ShipmentDash: React.FC<Props> = ({ className }) => {
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth() + 1); // Default to current month
  const [year, setYear] = useState<number>(new Date().getFullYear()); // Default to current year
  const [loading, setLoading] = useState<boolean>(false);
  const [dashboardData, setDashboardData] = useState<DashboardData>({
    total_booking_count: '0',
    pending_count: '0',
    collect_to_shipment_count: '0',
    shipment_started_count: '0',
    shipment_ended_count: '0',
    delivered_count: '0'
  });
  const [totalBookings, setTotalBookings] = useState(0);
  const [pending, setPending] = useState(0);
  const [collectToShipment, setCollectToShipment] = useState(0);
  const [totalStarted, setTotalStarted] = useState(0);
  const [totalEnded, setTotalEnded] = useState(0);
  const [totalDelivered, setTotalDelivered] = useState(0);
  


  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  useEffect(() => {
    fetchDashboardData(selectedMonth, year);
  }, [selectedMonth, year]);

  const animateCounter = (
    targetValue: number, 
    setState: React.Dispatch<React.SetStateAction<number>>
  ) => {
    let start = 0;
    const duration = 1500; // 1.5 seconds
    const steps = Math.max(1, Math.floor(duration / 50)); // Ensure at least 1 step
    const increment = Math.ceil(targetValue / steps); // Round up to avoid precision issues
  
    const updateCount = () => {
      start += increment;
      if (start >= targetValue) {
        setState(targetValue); // Ensure final value is correct
      } else {
        setState(start);
        requestAnimationFrame(updateCount);
      }
    };
  
    if (targetValue > 0) {
      requestAnimationFrame(updateCount);
    } else {
      setState(0);
    }
  };
  
  useEffect(() => {
    if (dashboardData) {
      animateCounter(Number(dashboardData.total_booking_count), setTotalBookings);
      animateCounter(Number(dashboardData.pending_count), setPending);
      animateCounter(Number(dashboardData.collect_to_shipment_count), setCollectToShipment);
      animateCounter(Number(dashboardData.shipment_started_count), setTotalStarted);
      animateCounter(Number(dashboardData.shipment_ended_count), setTotalEnded);
      animateCounter(Number(dashboardData.delivered_count), setTotalDelivered);
    }
  }, [dashboardData]);  
  


  const handlePrev = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setYear((prevYear) => prevYear - 1);
    } else {
      setSelectedMonth((prevIndex) => prevIndex - 1);
    }
  };

  const handleNext = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setYear((prevYear) => prevYear + 1);
    } else {
      setSelectedMonth((prevIndex) => prevIndex + 1);
    }
  };

  const fetchDashboardData = async (monthIndex: number, selectedYear: number) => {
    try {
      setLoading(true);

      const response = await fetch('https://backendcway.causewaycargo.com/superadmin/dashboarddetails', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          auth: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6Nywicm9sZSI6InN1cGVyX2FkbWluIiwiaWF0IjoxNzM3MDA4MTc1fQ.FF6USnon0RR_0FIWWJGbzQQN8hX_7uEdyn9vK_2iW1s',
        },
        body: JSON.stringify({
          month: `${months[monthIndex - 1]} ${selectedYear}`
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const result = await response.json();

      if (result.status === 'success' && result.data) {
        setDashboardData(result.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`card ${className}`}>
      {/* begin::Body */}
      <div className='card-body p-0'>
        {/* begin::Header */}
        <div className={`px-9 pt-7 card-rounded h-275px w-100 bg-white`}>
          {/* begin::Heading */}
          <div className='d-flex flex-stack'>
            <h3 className='m-0 text-dark fw-bolder fs-3'>Booking Details</h3>

            {/* Month Selector */}
            <div className="d-flex align-items-center">
              <span onClick={handlePrev} className="d-flex" style={{ cursor: 'pointer' }}>
                <KTSVG path='/media/icons/duotune/arrows/arr074.svg' className='svg-icon-2x svg-icon-primary d-block' />
              </span>
              <span className="month-display mx-2 text-dark fw-bold">
                {months[selectedMonth - 1]} {year}
              </span>
              <span className="d-flex" onClick={handleNext} style={{ cursor: 'pointer' }}>
                <KTSVG path='/media/icons/duotune/arrows/arr071.svg' className='svg-icon-2x svg-icon-primary d-block' />
              </span>
            </div>
          </div>
          {/* end::Heading */}
          {/* begin::Balance */}
          <div className='d-flex text-center flex-column text-dark pt-8'>
            <span className='fw-bold fs-7'>Total Bookings</span>
            <span className='fw-bolder fs-2x pt-1' style={{ color: '#951E1A' }}>{totalBookings}</span>
          </div>
          {/* end::Balance */}
        </div>
        {/* end::Header */}
        {/* begin::Items */}
        <div
          className=' card-rounded mx-9 mb-10 px-6 py-10 position-relative z-index-1'
          style={{ marginTop: '-100px', backgroundColor: '#F3F6F8' }}
        >
          {/* begin::Item */}
          <div className='d-flex align-items-center mb-6'>
            {/* begin::Symbol */}
            <div className='symbol symbol-45px w-40px me-5'>
              <span className='symbol-label bg-lighten'>
                {/* <KTSVG path='/media/icons/duotune/maps/map004.svg' className='svg-icon-1' /> */}
                <img alt='customer' src={toAbsoluteUrl('/media/icons/duotune/general/booking1.svg')} style={{ width: '24px' }} />
              </span>
            </div>
            {/* end::Symbol */}
            {/* begin::Description */}
            <div className='d-flex align-items-center flex-wrap w-100'>
              {/* begin::Title */}
              <div className='mb-1 pe-3 flex-grow-1'>
                <a href='#' className='fs-5 text-gray-800 text-hover-primary fw-bolder'>
                  Pending
                </a>
                <div className='text-gray-400 fw-bold fs-7'>Awaiting Processing</div>
              </div>
              {/* end::Title */}
              {/* begin::Label */}
              <div className='d-flex align-items-center'>
                <div className='fw-bolder fs-5 text-gray-800 pe-1'>{pending}</div>
                {/* <KTSVG
                  path='/media/icons/duotune/arrows/arr066.svg'
                  className='svg-icon-5 svg-icon-success ms-1'
                /> */}
              </div>
              {/* end::Label */}
            </div>
            {/* end::Description */}
          </div>
          {/* end::Item */}

          <div className='d-flex align-items-center mb-6'>
            {/* begin::Symbol */}
            <div className='symbol symbol-45px w-40px me-5'>
              <span className='symbol-label bg-lighten'>
                <img alt='customer' src={toAbsoluteUrl('/media/icons/duotune/general/booking2.svg')} style={{ width: '24px' }} />
              </span>
            </div>
            {/* end::Symbol */}
            {/* begin::Description */}
            <div className='d-flex align-items-center flex-wrap w-100'>
              {/* begin::Title */}
              <div className='mb-1 pe-3 flex-grow-1'>
                <a href='#' className='fs-5 text-gray-800 text-hover-primary fw-bolder'>
                  Collect to Shipment
                </a>
                <div className='text-gray-400 fw-bold fs-7'>Ready for Collection</div>
              </div>
              {/* end::Title */}
              {/* begin::Label */}
              <div className='d-flex align-items-center'>
                <div className='fw-bolder fs-5 text-gray-800 pe-1'>{collectToShipment}</div>
                {/* <KTSVG
                  path='/media/icons/duotune/arrows/arr066.svg'
                  className='svg-icon-5 svg-icon-success ms-1'
                /> */}
              </div>
              {/* end::Label */}
            </div>
            {/* end::Description */}
          </div>
          {/* begin::Item */}
          <div className='d-flex align-items-center mb-6'>
            {/* begin::Symbol */}
            <div className='symbol symbol-45px w-40px me-5'>
              <span className='symbol-label bg-lighten'>
                <img alt='customer' src={toAbsoluteUrl('/media/icons/duotune/general/booking3.svg')} style={{ width: '24px' }} />
              </span>
            </div>
            {/* end::Symbol */}
            {/* begin::Description */}
            <div className='d-flex align-items-center flex-wrap w-100'>
              {/* begin::Title */}
              <div className='mb-1 pe-3 flex-grow-1'>
                <a href='#' className='fs-5 text-gray-800 text-hover-primary fw-bolder'>
                  Shipment Started
                </a>
                <div className='text-gray-400 fw-bold fs-7'>In Transit</div>
              </div>
              {/* end::Title */}
              {/* begin::Label */}
              <div className='d-flex align-items-center'>
                <div className='fw-bolder fs-5 text-gray-800 pe-1'>{totalStarted}</div>
                {/* <KTSVG
                  path='/media/icons/duotune/general/gen024.svg'
                  className='svg-icon-5 svg-icon-primary ms-1'
                /> */}
              </div>
              {/* end::Label */}
            </div>
            {/* end::Description */}
          </div>
          {/* end::Item */}
          {/* begin::Item */}
          <div className='d-flex align-items-center mb-6'>
            {/* begin::Symbol */}
            <div className='symbol symbol-45px w-40px me-5'>
              <span className='symbol-label bg-lighten'>
                <img alt='customer' src={toAbsoluteUrl('/media/icons/duotune/general/booking5.svg')} style={{ width: '24px' }} />
              </span>
            </div>
            {/* end::Symbol */}
            {/* begin::Description */}
            <div className='d-flex align-items-center flex-wrap w-100'>
              {/* begin::Title */}
              <div className='mb-1 pe-3 flex-grow-1'>
                <a href='#' className='fs-5 text-gray-800 text-hover-primary fw-bolder'>
                  Shipment Ended
                </a>
                <div className='text-gray-400 fw-bold fs-7'>Arrived</div>
              </div>
              {/* end::Title */}
              {/* begin::Label */}
              <div className='d-flex align-items-center'>
                <div className='fw-bolder fs-5 text-gray-800 pe-1'>{totalEnded}</div>
                {/* <KTSVG
                  path='/media/icons/duotune/arrows/arr066.svg'
                  className='svg-icon-5 svg-icon-success ms-1'
                /> */}
              </div>
              {/* end::Label */}
            </div>
            {/* end::Description */}
          </div>
          {/* end::Item */}
          {/* begin::Item */}
          <div className='d-flex align-items-center'>
            {/* begin::Symbol */}
            <div className='symbol symbol-45px w-40px me-5'>
              <span className='symbol-label bg-lighten'>
                <img alt='customer' src={toAbsoluteUrl('/media/icons/duotune/general/booking4.svg')} style={{ width: '24px' }} />
              </span>
            </div>
            {/* end::Symbol */}
            {/* begin::Description */}
            <div className='d-flex align-items-center flex-wrap w-100'>
              {/* begin::Title */}
              <div className='mb-1 pe-3 flex-grow-1'>
                <a href='#' className='fs-5 text-gray-800 text-hover-primary fw-bolder'>
                  Delivered
                </a>
                <div className='text-gray-400 fw-bold fs-7'>Completed Orders</div>
              </div>
              {/* end::Title */}
              {/* begin::Label */}
              <div className='d-flex align-items-center'>
                <div className='fw-bolder fs-5 text-gray-800 pe-1'>{totalDelivered}</div>
                {/* <KTSVG
                  path='/media/icons/duotune/arrows/arr065.svg'
                  className='svg-icon-5 svg-icon-success ms-1'
                /> */}
              </div>
              {/* end::Label */}
            </div>

            {/* end::Description */}
          </div>
          {/* end::Item */}
        </div>
        {/* end::Items */}
      </div>
      {/* end::Body */}
    </div>
  )
}

export { ShipmentDash }